import React, { useEffect, useState } from "react";
import axios from "axios";
import "./ProfilePage.css";
import { getUserEmail } from "../../../utils/auth";
import BASE_URL from "../../../config/apiConfig";

const ProfilePage = () => {
  const [user, setUser] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    const email = getUserEmail();

    if (!email) {
      setLoadingProfile(false);
      setLoadingTransactions(false);
      setError("Unable to load your profile. Please sign in again.");
      return;
    }

    const fetchTransactions = async () => {
      try {
        const response = await axios.post(
          `${BASE_URL}/api/user/get-transactions-by-email`,
          { email },
        );
        setTransactions(response.data || []);
      } catch (err) {
        setError("Unable to load transaction history.");
      } finally {
        setLoadingTransactions(false);
      }
    };

    const fetchProfile = async () => {
      try {
        const response = await axios.post(`${BASE_URL}/api/user/auth/profile`, {
          email,
        });

        const profileUser = response.data?.user || {};
        let parsedSecondaryEmails = profileUser.secondary_emails;

        if (typeof parsedSecondaryEmails === "string") {
          try {
            parsedSecondaryEmails = JSON.parse(parsedSecondaryEmails);
          } catch {
            parsedSecondaryEmails = [];
          }
        }

        setUser(profileUser);
        setSubscriptions(response.data?.subscriptions || []);
        setSecondaryEmails(
          Array.isArray(parsedSecondaryEmails) ? parsedSecondaryEmails : [],
        );
      } catch (err) {
        setError("Unable to load profile information.");
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
    fetchTransactions();
  }, []);

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const addSecondaryEmail = async () => {
    const email = newEmail.trim();

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (email.toLowerCase() === (user.email || "").toLowerCase()) {
      setError("This is already your primary email address.");
      return;
    }

    if (
      secondaryEmails.some((item) => item.toLowerCase() === email.toLowerCase())
    ) {
      setError("This email has already been added.");
      return;
    }

    try {
      setError("");

      const response = await axios.post(
        `${BASE_URL}/api/user/auth/add-secondary-email`,
        {
          user_email: user.email,
          secondary_emails: [email],
        },
      );

      setSecondaryEmails(response.data?.secondary_emails || []);
      setNewEmail("");
      setShowInput(false);
    } catch {
      setError("Failed to add secondary email. Please try again.");
    }
  };

  const deleteEmail = async (emailToDelete) => {
    try {
      setError("");

      const response = await axios.post(
        `${BASE_URL}/api/user/auth/delete-secondary-email`,
        {
          user_email: user.email,
          email_to_delete: emailToDelete,
        },
      );

      setSecondaryEmails(response.data?.secondary_emails || []);
    } catch {
      setError("Failed to delete email. Please try again.");
    }
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const address = subscriptions?.[0]?.address || "No address available.";
  const userInitial = (user.name || "U").trim().charAt(0).toUpperCase();

  return (
    <div className="farmology-profile-page">
      <section className="profile-page-hero">
        <div className="profile-hero-inner">
          <div className="profile-hero-copy">
            <span className="profile-hero-eyebrow">FARMOLOGY ACCOUNT</span>
            <h1>Your profile.</h1>
            <p>
              Manage your account details, contact information, and payment
              history from one place.
            </p>
          </div>

          <div className="profile-hero-meta">
            <span>ACCOUNT</span>
            <strong>{user.name || "FARMER"}</strong>
          </div>
        </div>
      </section>

      <main className="profile-page-content">
        <section className="profile-intro">
          <div>
            <span className="profile-eyebrow">ACCOUNT OVERVIEW</span>
            <h2>Personal details</h2>
            <p>
              Your Farmology account information and registered contact details.
            </p>
          </div>

          <div className="profile-status">
            <span className="profile-status-dot" />
            <span>Account active</span>
          </div>
        </section>

        {error && (
          <div className="profile-alert" role="alert">
            <strong>Something needs attention</strong>
            <span>{error}</span>
          </div>
        )}

        <section className="profile-overview-grid">
          <article className="profile-card profile-user-card">
            <div className="profile-card-label">PROFILE</div>

            <div className="profile-user-head">
              <div className="profile-avatar">
                <img src="/Images/defaultProfileDp.png" alt="Profile" />
                <span>{userInitial}</span>
              </div>

              <div>
                <h3>{user.name || "User"}</h3>
                <p>{user.email || "Email not available"}</p>
              </div>
            </div>

            <div className="profile-info-list">
              <div className="profile-info-item">
                <span>Primary email</span>
                <strong>{user.email || "N/A"}</strong>
              </div>

              <div className="profile-info-item">
                <span>Phone</span>
                <strong>{user.phone || "N/A"}</strong>
              </div>

              <div className="profile-info-item">
                <span>Joined on</span>
                <strong>{formatDate(user.created_at)}</strong>
              </div>
            </div>
          </article>

          <article className="profile-card profile-address-card">
            <div className="profile-card-label">DETAILS</div>

            <div className="profile-card-heading">
              <div>
                <h3>Registered address</h3>
                <p>Address associated with your Farmology subscription.</p>
              </div>
            </div>

            <div className="profile-address-box">
              <span>ADDRESS</span>
              <strong>{address}</strong>
            </div>
          </article>

          <article className="profile-card profile-email-card">
            <div className="profile-card-label">CONTACT</div>

            <div className="profile-card-heading profile-email-heading">
              <div>
                <h3>Secondary emails</h3>
                <p>Add another email for account communication.</p>
              </div>

              <button
                type="button"
                className="profile-add-email-button"
                onClick={() => {
                  setError("");
                  setShowInput((value) => !value);
                }}
              >
                {showInput ? "Cancel" : "+ Add email"}
              </button>
            </div>

            {showInput && (
              <div className="profile-email-form">
                <input
                  type="email"
                  placeholder="Enter secondary email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addSecondaryEmail();
                  }}
                  className="profile-email-input"
                  autoComplete="email"
                />
                <button
                  type="button"
                  onClick={addSecondaryEmail}
                  className="profile-submit-email-button"
                >
                  Add email
                </button>
              </div>
            )}

            {secondaryEmails.length > 0 ? (
              <div className="profile-secondary-list">
                {secondaryEmails.map((email, index) => (
                  <div
                    className="profile-secondary-row"
                    key={`${email}-${index}`}
                  >
                    <div>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{email}</strong>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteEmail(email)}
                      className="profile-delete-email-button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="profile-empty-inline">
                No secondary emails added yet.
              </div>
            )}
          </article>
        </section>

        <section className="profile-transactions-section">
          <div className="profile-section-heading">
            <div>
              <span className="profile-eyebrow">PAYMENT ACTIVITY</span>
              <h2>Transaction history</h2>
              <p>Review your previous Farmology payments and invoices.</p>
            </div>

            <div className="profile-transaction-count">
              <strong>{transactions.length}</strong>
              <span>transactions</span>
            </div>
          </div>

          <div className="profile-transactions-card">
            {loadingTransactions ? (
              <div className="profile-loading-state">
                <span className="profile-spinner" />
                <p>Loading transaction history...</p>
              </div>
            ) : transactions.length > 0 ? (
              <div className="profile-table-scroll">
                <table className="profile-transaction-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Amount</th>
                      <th>Plan</th>
                      <th>Crop</th>
                      <th>Method</th>
                      <th>Frequency</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>

                  <tbody>
                    {transactions.map((transaction, index) => (
                      <tr
                        key={
                          transaction.id || `${transaction.created_at}-${index}`
                        }
                      >
                        <td>{index + 1}</td>
                        <td className="profile-amount-cell">
                          ₹ {transaction.amount}
                        </td>
                        <td>{transaction.plan_name || "N/A"}</td>
                        <td>{transaction.crop_name || "N/A"}</td>
                        <td>{transaction.payment_method || "N/A"}</td>
                        <td>{transaction.payment_frequency || 0} Month(s)</td>
                        <td>
                          <span
                            className={`profile-transaction-status ${String(
                              transaction.status || "unknown",
                            ).toLowerCase()}`}
                          >
                            {transaction.status || "Unknown"}
                          </span>
                        </td>
                        <td>{formatDate(transaction.created_at)}</td>
                        <td>
                          {transaction.invoice_url ? (
                            <a
                              href={`${BASE_URL}${transaction.invoice_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="profile-invoice-button"
                            >
                              Invoice ↗
                            </a>
                          ) : (
                            <span className="profile-invoice-missing">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="profile-empty-state">
                <span className="profile-empty-icon">₹</span>
                <h3>No transactions yet</h3>
                <p>Your completed payments will appear here.</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProfilePage;
